import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import {
  answerBlock
} from 'actions';

import LocalisedMessage from 'components/Common/LocalisedMessage';
import Question from 'components/Questionnaires/Question';
import { QuestionTextField, QuestionDropDownField, QuestionCheckBoxField } from 'components/Questionnaires/fields';
import { requiredValidator, validateQuestion } from 'components/Questionnaires/validators';
import CollapsibleHeader from 'components/Questionnaires/CollapsibleHeader';

export const QuestionSignerBlock = ({ block, preQualAnswers }) => {
  const dispatch = useDispatch();
  const { capacityInfo } = block.componentProps ?? {};
  const showCapacity = block.componentProps?.entityOnly || block.componentProps?.individualOnly;
  const [collapsibleAreaShown, setCollapsibleAreaShown] = useState(false);

  const collectableSupportingDocs = useSelector(state => state?.form?.accCollectableSupportingDocs);
  const { formType, formGroupType } = useParams();
  const isWForm = (formType && formType?.indexOf('w8') > -1) || (formGroupType && formGroupType?.indexOf('w8') > -1);
  let allowAffidavit = false;

  if (collectableSupportingDocs) {
    const affidavit = collectableSupportingDocs.find(item => item.key === 'AffidavitOfUnchangedStatus');
    if (affidavit && isWForm) {
      allowAffidavit = true;
    }
  }

  const defaultAnswer = () => {
    let defaultCapacity = 'Signing on Own Behalf';
    if (!showCapacity || block.componentProps?.entityOnly || (preQualAnswers && preQualAnswers.individual !== undefined && preQualAnswers.individual === false)) {
      defaultCapacity = undefined;
    }
    return { capacity: defaultCapacity };
  };

  const [answer, setAnswer] = useState(block.answers ?? defaultAnswer());

  const [name, setName] = useState({
    id: 'name',
    component: QuestionTextField,
    label: 'questionnaire.signer.name.label',
    info: {
      instructions: 'questionnaire.signer.name.info',
    },
    visible: true,
    validation: {
      conditions: [
        { condition: requiredValidator, message: 'questionnaire.signer.name.errorMessage' }
      ]
    },
    answer: answer?.name
  });
  const individualCapacity = {
    id: 'capacity',
    component: QuestionDropDownField,
    label: 'questionnaire.signer.capacity.label',
    componentProps: {
      items: 'questionnaire.signer.capacities.individual',
      preventTranslation: true
    },
    default: 'Signing on Own Behalf',
    info: {
      instructions: capacityInfo ?? 'questionnaire.signer.capacity.info',
    },
    visible: (bAnswers, qAnswers, pAnswers) => (showCapacity
      ? block?.componentProps?.individualOnly === true || (!block?.componentProps?.individualOnly && !block?.componentProps?.entityOnly)
      : false),
    answer: answer?.capacity
  };
  const [entityCapacity, setEntityCapacity] = useState({
    id: 'capacity',
    label: 'questionnaire.signer.capacity.label',
    component: QuestionDropDownField,
    componentProps: {
      items: 'questionnaire.signer.capacities.entity',
      preventTranslation: true
    },
    info: {
      instructions: capacityInfo ?? 'questionnaire.signer.capacity.info',
    },
    visible: (bAnswers, qAnswers, pAnswers) => (showCapacity ? block?.componentProps?.entityOnly === true : false),
    validation: {
      conditions: [
        { condition: requiredValidator, message: 'questionnaire.signer.capacity.errorMessage' }
      ]
    },
    answer: answer?.capacity
  });
  const [otherCapacity, setOtherCapacity] = useState({
    id: 'otherCapacity',
    component: QuestionTextField,
    label: 'questionnaire.signer.otherCapacity.label',
    visible: (bAnswers, qAnswers, pAnswers) => (showCapacity ? bAnswers?.capacity === 'Other' : false),
    validation: {
      conditions: [
        { condition: requiredValidator, message: 'questionnaire.signer.otherCapacity.errorMessage' }
      ]
    },
    answer: answer?.otherCapacity
  });
  const backupWhd = {
    id: 'subjectToBackupWhd',
    component: QuestionCheckBoxField,
    componentProps: {
      option: 'questionnaire.signer.backupWhd.label',
    },
    visible: true,
    answer: answer?.subjectToBackupWhd
  };

  const affidavit = {
    id: 'generateAffidavit',
    label: 'questionnaire.signer.generateAffidavit.label',
    component: QuestionCheckBoxField,
    componentProps: {
      option: 'questionnaire.signer.generateAffidavit',
    },
    visible: allowAffidavit,
    answer: answer?.subjectToBackupWhd
  };

  const blockIdentifier = useMemo(() => ((block && block.id) ? `${ block.id }${ block.index !== undefined ? (`.${ block.index }`) : '' }` : undefined), [block.index]);

  const onQuestionChange = (q, value) => {
    switch (q.id) {
      case 'name': {
        const newSigner = { ...q, answer: value };
        validateQuestion(newSigner);
        setName(newSigner);
        setAnswer({ ...answer, name: value });
        break;
      }
      case 'capacity': {
        let changes = {};
        if (block.componentProps?.entityOnly === true) {
          const newCapacity = { ...q, answer: value };
          validateQuestion(newCapacity);
          setEntityCapacity(newCapacity);
        }
        if (value !== 'Other' && otherCapacity.answer !== undefined) {
          changes = { otherCapacity: undefined };
          const newOther = { ...otherCapacity, answer: undefined, isValid: undefined };
          validateQuestion(newOther);
          setOtherCapacity(newOther);
        }
        setAnswer({ ...answer, capacity: value, ...changes });
        break;
      }
      case 'otherCapacity': {
        const newOther = { ...q, answer: value };
        validateQuestion(newOther);
        setOtherCapacity(newOther);
        setAnswer({ ...answer, otherCapacity: value });
        break;
      }
      case 'subjectToBackupWhd':
        setAnswer({ ...answer, subjectToBackupWhd: value });
        break;
      case 'generateAffidavit':
        setAnswer({ ...answer, generateAffidavit: value });
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    let isValid = name.isValid && (answer.capacity !== 'Other' || otherCapacity.isValid);
    if (block.componentProps?.entityOnly === true) {
      isValid = isValid && entityCapacity.isValid;
    }
    dispatch(answerBlock(block.id, answer, isValid));
  }, [answer]);

  useEffect(() => {
    if (name.answer !== block.answers?.name) {
      onQuestionChange(name, block.answers?.name);
    }
    if (block.componentProps?.entityOnly === true && entityCapacity.answer !== block.answers?.capacity) {
      onQuestionChange(entityCapacity, block.answers?.capacity);
    }
    if (otherCapacity.answer !== block.answers?.otherCapacity) {
      onQuestionChange(otherCapacity, block.answers?.otherCapacity);
    }
    setAnswer(block.answers ?? {});
  }, [block.answers]);

  useEffect(() => {
    if (block.isForceEdited === true) {
      if (answer?.name === undefined || answer.name === '') {
        onQuestionChange({ ...name, isForceEdited: true }, '');
      }
      if (block.componentProps?.entityOnly === true && (answer?.capacity === undefined || answer.capacity === '')) {
        onQuestionChange({ ...entityCapacity, isForceEdited: true }, '');
      }
      if (answer?.capacity === 'Other' && (answer?.otherCapacity === undefined || answer?.otherCapacity === '')) {
        onQuestionChange({ ...otherCapacity, isForceEdited: true }, '');
      }
    }
  }, [block.isForceEdited]);

  return (<React.Fragment>
    <Question key={ name.id } blockId={ blockIdentifier } question={ name } { ...name.componentProps } onChange={ (q, a) => onQuestionChange(q, a) } />
    { individualCapacity.visible(block.answers, {}, preQualAnswers) && <Question
      key={ individualCapacity.id }
      blockId={ blockIdentifier }
      question={ individualCapacity }
      { ...individualCapacity.componentProps }
      onChange={ (q, a) => onQuestionChange(q, a) }
    /> }
    { entityCapacity.visible(block.answers, {}, preQualAnswers) && <Question key={ entityCapacity.id } blockId={ blockIdentifier } question={ entityCapacity } { ...entityCapacity.componentProps } onChange={ (q, a) => onQuestionChange(q, a) } /> }
    { otherCapacity.visible(block.answers, {}, preQualAnswers) && <Question key={ otherCapacity.id } blockId={ blockIdentifier } question={ otherCapacity } { ...otherCapacity.componentProps } onChange={ (q, a) => onQuestionChange(q, a) } /> }
    { block.componentProps?.showBackupWhd === true && <div className="col-12">
      <CollapsibleHeader isShown={ collapsibleAreaShown } onExpandCollapse={ value => setCollapsibleAreaShown(value) } text="questionnaire.signer.additionalCertification" />
      { collapsibleAreaShown && <Question key={ backupWhd.id } blockId={ blockIdentifier } question={ backupWhd } { ...backupWhd.componentProps } onChange={ (q, a) => onQuestionChange(q, a) } /> }
    </div> }
    { !showCapacity && <p className="mt-2 col-12"><LocalisedMessage id="questionnaire.signer.certification" /></p> }
    { allowAffidavit && <Question key={ affidavit.id } blockId={ blockIdentifier } question={ affidavit } { ...affidavit.componentProps } onChange={ (q, a) => onQuestionChange(q, a) } /> }
  </React.Fragment>);
};

QuestionSignerBlock.displayName = 'QuestionSignerBlock';

QuestionSignerBlock.propTypes = {
  block: PropTypes.shape().isRequired,
  preQualAnswers: PropTypes.shape()
};

QuestionSignerBlock.defaultProps = {
  preQualAnswers: null
};

export default QuestionSignerBlock;
